import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { CreateChancel, DateCalendarForm, Input, SelectAutocompletePagination } from 'components';
import { clientStatusEnums, FindLoad, FindSuccess, renderStaffName, useModal } from 'utils';
import { clientActions, httpRequestsOnSuccessActions } from 'store';

/**
 * Create / edit modal for a client → staff assignment. Opened via `useModal`.
 * Pass `item` (an assigned-staff row) to edit; omit it to create.
 *
 * Edit mode reuses the same form but the staff is fixed (no select) — only the
 * role and assignment date can change — mirroring the former `EditAssignedStaff`.
 */
export const AssignStaffToClient = ({ item }) => {
    const isEdit = !!item;
    const ACTION_TYPE = isEdit ? 'EDIT_ASSIGNED_STAFF' : 'ASSIGN_STAFF';
    const client = useSelector((state) => state.client.clientItemInfo);
    const clientStaffs = useSelector((state) => state.client.clientStaffs);
    const dispatch = useDispatch();
    const { close } = useModal();
    const loader = FindLoad(ACTION_TYPE);
    const success = FindSuccess(ACTION_TYPE);
    const { handleSubmit, control, reset } = useForm({
        defaultValues: {
            staff: null,
            role: '',
            assignmentDate: null,
        },
    });

    useEffect(() => {
        if (item) {
            reset({
                staff: item?.staffId,
                role: item?.staffRole,
                assignmentDate: item?.assignmentDate || null,
            });
        }
    }, [item]);

    useEffect(() => {
        if (success?.length) {
            close();
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
        }
    }, [success]);

    const filterList = (staffs) => {
        return staffs?.filter(
            (array_el) => clientStaffs?.filter((anotherOne_el) => anotherOne_el?.staffId === array_el?.id).length === 0
        );
    };

    const handleCreate = (data) => {
        if (isEdit) {
            const sendParams = {
                staffId: item?.staffId,
                clientId: client?.id,
                role: data?.role,
                assignmentDate: data?.assignmentDate,
            };
            dispatch(clientActions.editAssignedStaff(item?.assignedId, sendParams, client?.id));
        } else {
            const sendParams = {
                staffId: data?.staff,
                clientId: client?.id,
                role: data?.role,
                assign: true,
            };
            data?.assignmentDate && (sendParams['assignmentDate'] = data?.assignmentDate);
            dispatch(clientActions.assignStaff(sendParams));
        }
    };

    return (
        <form onSubmit={handleSubmit(handleCreate)} style={{ width: 400 }}>
            <p className="modal-header-title">{isEdit ? 'Edit Assigned Staff' : 'Assign Staff'}</p>
            <div className="modal-body-wrapper flex-align-start" style={{ gap: 24 }}>
                <div className="full-width">
                    {!isEdit && (
                        <Controller
                            name="staff"
                            control={control}
                            rules={{ required: 'Staff member is required.' }}
                            render={({ field, fieldState }) => (
                                <SelectAutocompletePagination
                                    style={'full-width'}
                                    filterOptions={(options) => filterList(options)}
                                    defaultStatuses={[clientStatusEnums.ACTIVE]}
                                    title={'Staff Member*'}
                                    placeholder={'Select Staff Member*'}
                                    name={'staff'}
                                    handleSelect={(e) => field.onChange(e.target.value)}
                                    defaultValue={field.value}
                                    renderValue={(i) => renderStaffName(i)}
                                    type={'staff'}
                                    error={fieldState.error ? 'staff' : ''}
                                    typeError={fieldState.error?.message}
                                />
                            )}
                        />
                    )}
                    <Input
                        name="role"
                        label="Staff Role*"
                        variant="outlined"
                        control={control}
                        maxLength={20}
                        rules={{
                            maxLength: { value: 20, message: 'Max length is 20.' },
                            required: 'Staff role is required.',
                        }}
                    />
                    <DateCalendarForm name={'assignmentDate'} label={'Assignment Date'} control={control} />
                </div>
            </div>
            <CreateChancel
                type="submit"
                loader={!!loader.length}
                create={isEdit ? 'Save' : 'Assign Staff'}
                chancel={'Cancel'}
                onClose={close}
                buttonWidth="100%"
            />
        </form>
    );
};
